import { Create, Edit, required, Resource, TextInput, useGetOne, useRedirect } from "react-admin";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { DrivingSchool } from "../providers/drivingSchoolsProvider";
import { Column } from "../misc/Column";
import Box from "@mui/material/Box";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Row } from "../misc/Row";
import { Form2 } from "../misc/Form2";
import { LessonsScheduleInput } from "../inputs/LessonsScheduleInput";
import { grants } from "../backoffice.access_control";
import { AccessDenied } from "../misc/AccessDenied";
import { type Timetable } from "../providers/timetablesProvider";
import { SaveButton2 } from "../buttons/SaveButton2";
import { DeleteRecordButton } from "../buttons/DeleteRecordButton";
import { CloneButton2 } from "../buttons/CloneButton2";
import { useCurrentDrivingSchoolId } from "../hooks/useCurrentDrivingSchoolId";
import { DefaultList } from "../misc/DefaultList";
import { RecordDetailsHeader } from "../misc/RecordDetailsHeader";

function CreateTimetableForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();
  const { data: drivingSchool } = useGetOne<DrivingSchool>(
    "drivingSchools",
    { id: drivingSchoolId! },
    { enabled: !!drivingSchoolId },
  );

  if (!grants.has("Timetable:create")) {
    return <AccessDenied />;
  }

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  if (!drivingSchool) {
    return <LoadingIndicator />;
  }

  return (
    <Create
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Stundenplan erfolgreich erstellt.");
          redirect(`/drivingSchools/${drivingSchoolId}/timetables`);
        },
        onError: (error: Error) => {
          handleError("Fehler beim Erstellen des Stundenplans.", error);
        },
      }}
      transform={(data: Omit<Timetable, "drivingSchoolId">): Timetable => ({
        ...data,
        drivingSchoolId,
      })}
    >
      <TimetableForm mode="create" />
    </Create>
  );
}

function EditTimetableForm() {
  const redirect = useRedirect();
  const timetable = useRecordAndSwitchDrivingSchoolIfNecessary<Timetable>((timetable) => timetable.drivingSchoolId);

  if (!grants.has("Timetable:edit")) {
    return <AccessDenied />;
  }

  if (!timetable) {
    return <LoadingIndicator />;
  }

  return (
    <Edit
      title={
        <BackofficeRecordTitle
          header={
            <RecordDetailsHeader
              backLabel="Stundenpläne"
              backUrl={`/drivingSchools/${timetable.drivingSchoolId}/timetables`}
              title={timetable.name}
            />
          }
        />
      }
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert.");
          redirect(`/drivingSchools/${timetable.drivingSchoolId}/timetables`);
        },
        onError: (error: unknown) => {
          handleError("Fehler beim Speichern.", error);
        },
      }}
    >
      <TimetableForm mode="edit" />
    </Edit>
  );
}

function TimetableForm({ mode }: { mode: "create" | "edit" }) {
  const drivingSchoolId = useCurrentDrivingSchoolId();
  return (
    <Form2 warnWhenUnsavedChanges>
      <Column sx={{ padding: overviewCardStyle.padding }}>
        <TextInput label="Name" source="name" autoFocus={mode === "create"} validate={required()} />
        <LessonsScheduleInput source="lessonsSchedule" />
        <Row sx={{ flexWrap: { xs: "wrap", sm: "nowrap" }, gap: 1, pt: 1, justifyContent: "space-between" }}>
          <SaveButton2 />
          {mode === "edit" && (
            <Row gap={1} sx={{ ml: { xs: "auto", sm: 0 } }}>
              <CloneButton2 />
              <DeleteRecordButton onSuccessRedirectTo={`/drivingSchools/${drivingSchoolId}/timetables`} />
            </Row>
          )}
        </Row>
      </Column>
    </Form2>
  );
}

export const timetablesResource = (
  <Resource
    key="timetables"
    name="timetables"
    list={DefaultList}
    create={CreateTimetableForm}
    edit={EditTimetableForm}
    options={{
      label: "Stundenpläne",
      createTitle: "Neuer Stundenplan",
      noEntriesText: "Noch kein Stundenplan hinterlegt.",
      noMatchingEntriesText: "Keine passenden Stundenpläne gefunden.",
    }}
    recordRepresentation={(timetable: Timetable) => timetable.name}
  />
);
